<script setup>
import { ref, computed, defineEmits } from 'vue'
import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue'

const emailQuery = ref('')
const showSuggestions = ref(true)

const emailExtensions = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'googlemail.com',
  'mail.com',
  'live.com',
  'msn.com',
]

const emailSuggestions = computed(() => {
  if (!emailQuery.value) {
    return []
  }

  const atIndex = emailQuery.value.indexOf('@')

  if (atIndex === -1) {
    return emailExtensions.map((ext) => `${emailQuery.value}@${ext}`)
  }

  if (showSuggestions.value) {
    const username = emailQuery.value.slice(0, atIndex)
    const domain = emailQuery.value.slice(atIndex + 1)

    return emailExtensions.reduce((arr, ext) => {
      if (ext.startsWith(domain) && domain !== ext)
        arr.push(`${username}@${ext}`)
      return arr
    }, [])
  }

  return []
})

const props = defineProps({
  email: String,
  showLabel: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  autocomplete: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:email', 'focus'])

function handleEmailInput(e) {
  emailQuery.value = e.target.value
  emit('update:email', emailQuery.value)
  showSuggestions.value = true
}

function handleEmailSelect(val) {
  emailQuery.value = val
  emit('update:email', val)
  showSuggestions.value = false
}

function handleFocus() {
  showSuggestions.value = true
  emit('focus')
}

function handleBlur() {
  setTimeout(() => {
    showSuggestions.value = false
  }, 150)
}
</script>

<template>
  <div>
    <label
      v-if="showLabel"
      for="email"
      class="text-white text-opacity-80 text-sm lg:text-base leading-[1]"
      >Email Address</label
    >
    <div class="mt-[5px] lg:mt-[10px] relative">
      <Combobox v-model="emailQuery" @update:modelValue="handleEmailSelect">
        <div class="relative">
          <ComboboxInput
            id="email"
            name="email"
            autocomplete="email"
            type="email"
            required
            :placeholder="placeholder"
            class="block w-full h-[50px] pl-[25px] pr-3 bg-[#0E122A] py-[18px] text-white rounded-[15px] border border-[#111631]"
            :displayValue="() => email"
            @input="handleEmailInput"
            @blur="handleBlur"
            @focus="handleFocus"
          />
        </div>
        <transition
          v-if="autocomplete"
          enter-active-class="transition duration-100 ease-out"
          enter-from-class="transform scale-95 opacity-0"
          enter-to-class="transform scale-100 opacity-100"
          leave-active-class="transition duration-75 ease-out"
          leave-from-class="transform scale-100 opacity-100"
          leave-to-class="transform scale-95 opacity-0"
        >
          <ComboboxOptions
            v-if="emailQuery && emailSuggestions.length && showSuggestions"
            class="absolute z-10 mt-1 w-full max-h-[168px] overflow-y-auto rounded-[15px] bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm md:mt-1 bottom-[calc(100%+5px)] mb-1"
            static
          >
            <ComboboxOption
              v-for="(suggestion, idx) in emailSuggestions"
              :key="suggestion"
              :value="suggestion"
              as="template"
              v-slot="{ active }"
            >
              <li
                class="no-underline"
                :class="[
                  active ? 'bg-purple-600' : '',
                  'cursor-pointer select-none py-1.5 px-4',
                  idx === 4 ? 'rounded-b-[15px]' : '',
                  idx === 0 ? 'rounded-t-[15px]' : '',
                ]"
              >
                {{ suggestion }}
              </li>
            </ComboboxOption>
          </ComboboxOptions>
        </transition>
      </Combobox>
    </div>
  </div>
</template>
